import React from "react";
import {
  Box,
  Container,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Fade,
  Select,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  IconButton,
} from "@mui/material";
import {
  Search,
  FilterList,
  LocalFireDepartment,
  ViewInAr,
  Clear,
  SortByAlpha,
  ArrowUpward,
  ArrowDownward,
  GridView,
  ViewList,
  NavigateNext,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductCard, { type Product } from "../components/products/ProductCard";
import ProductModal from "../components/products/ProductModal";
import styled from "styled-components";

const HeroSection = styled(Box)`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  padding: 80px 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle at 30% 50%,
      rgba(211, 47, 47, 0.1) 0%,
      transparent 50%
    );
  }
`;

const FilterPaper = styled(Paper)`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const StyledToggleButton = styled(ToggleButton)`
  &.Mui-selected {
    background: linear-gradient(135deg, #d32f2f, #ff6b6b);
    color: white;

    &:hover {
      background: linear-gradient(135deg, #c62828, #ff5252);
    }
  }
`;

// File-based product list loaded from /public/data
type ProductListItem = Pick<
  Product,
  | "id"
  | "title"
  | "price"
  | "image"
  | "fireRating"
  | "category"
  | "slug"
  | "model3d"
>;

const useProducts = () => {
  const [items, setItems] = React.useState<ProductListItem[]>([]);
  React.useEffect(() => {
    fetch("/data/products/index.json", { cache: "no-store" })
      .then((r) => r.json())
      .then((json) => setItems(Array.isArray(json.items) ? json.items : []))
      .catch(() => setItems([]));
  }, []);
  return items;
};

const Products: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");
  const [category, setCategory] = React.useState("all");
  const [rating, setRating] = React.useState("all");
  const [has3D, setHas3D] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("name");
  const [viewMode, setViewMode] = React.useState("grid");
  const [selected, setSelected] = React.useState<Product | null>(null);
  const [categories, setCategories] = React.useState<
    { value: string; key: string }[]
  >([]);

  React.useEffect(() => {
    fetch("/data/categories/index.json", { cache: "no-store" })
      .then((r) => r.json())
      .then((json) =>
        setCategories(Array.isArray(json.items) ? json.items : [])
      )
      .catch(() => setCategories([]));
  }, []);

  const products = useProducts();
  const filtered = products
    .filter((p) => {
      return (
        (category === "all" || p.category === category) &&
        (rating === "all" || p.fireRating === rating) &&
        (!has3D || Boolean(p.model3d)) &&
        (search.trim() === "" ||
          p.title.toLowerCase().includes(search.toLowerCase()))
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.title.localeCompare(b.title);
        case "price-asc":
          return (a.price || 0) - (b.price || 0);
        case "price-desc":
          return (b.price || 0) - (a.price || 0);
        default:
          return 0;
      }
    });

  const clearFilters = () => {
    setSearch("");
    setCategory("all");
    setRating("all");
    setHas3D(false);
    setSortBy("name");
  };

  const activeFiltersCount = [
    category !== "all",
    rating !== "all",
    has3D,
    search.trim() !== "",
  ].filter(Boolean).length;

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Breadcrumbs
            separator={<NavigateNext fontSize="small" />}
            sx={{ mb: 3, color: "rgba(255,255,255,0.7)" }}
          >
            <Link
              component={RouterLink}
              to="/"
              sx={{ color: "rgba(255,255,255,0.7)", textDecoration: "none" }}
            >
              {t("productDetail.breadcrumbs.home")}
            </Link>
            <Typography sx={{ color: "white" }}>{t("nav.products")}</Typography>
          </Breadcrumbs>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
            }}
          >
            {t("products.title")}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.8)",
              mb: 4,
              fontWeight: 400,
              maxWidth: 800,
            }}
          >
            {t("products.subtitle")}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip
              icon={<LocalFireDepartment />}
              label={t("products.chips.fireRange")}
              sx={{
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
            <Chip
              label={t("products.chips.certified")}
              sx={{
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
            <Chip
              label={t("products.chips.warranty")}
              sx={{
                backgroundColor: "rgba(255,255,255,0.1)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
          </Stack>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Filter Section */}
        <FilterPaper elevation={0}>
          <Stack spacing={3}>
            {/* Search and View Controls */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                fullWidth
                placeholder={t("products.filter.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: { md: 400 } }}
              />

              <Box sx={{ flex: 1 }} />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("products.sort.label")}</InputLabel>
                <Select
                  value={sortBy}
                  label={t("products.sort.label")}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SortByAlpha fontSize="small" />
                      <span>{t("products.sort.name")}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="price-asc">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ArrowUpward fontSize="small" />
                      <span>{t("products.sort.priceAsc")}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="price-desc">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ArrowDownward fontSize="small" />
                      <span>{t("products.sort.priceDesc")}</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
                size="small"
              >
                <ToggleButton value="grid">
                  <GridView />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewList />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Divider />

            {/* Filter Controls */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("common.filters")}:
                </Typography>
                {activeFiltersCount > 0 && (
                  <Chip
                    label={`${activeFiltersCount}`}
                    size="small"
                    color="primary"
                  />
                )}
              </Stack>

              <ToggleButtonGroup
                exclusive
                value={category}
                onChange={(_, v) => v && setCategory(v)}
                size="small"
              >
                <StyledToggleButton value="all">
                  {t("products.filter.allCategories")}
                </StyledToggleButton>
                {categories.length > 0 ? (
                  categories.map((c) => (
                    <StyledToggleButton key={c.value} value={c.value}>
                      {t(c.key as any)}
                    </StyledToggleButton>
                  ))
                ) : (
                  <>
                    <StyledToggleButton value="Tek Kanat">
                      {t("products.filter.singleLeaf")}
                    </StyledToggleButton>
                    <StyledToggleButton value="Çift Kanat">
                      {t("products.filter.doubleLeaf")}
                    </StyledToggleButton>
                    <StyledToggleButton value="Cam Panelli">
                      {t("products.filter.withGlass")}
                    </StyledToggleButton>
                  </>
                )}
              </ToggleButtonGroup>

              <ToggleButtonGroup
                exclusive
                value={rating}
                onChange={(_, v) => v && setRating(v)}
                size="small"
              >
                <StyledToggleButton value="all">
                  <LocalFireDepartment fontSize="small" sx={{ mr: 0.5 }} />
                  {t("products.filter.allClasses")}
                </StyledToggleButton>
                <StyledToggleButton value="EI30">EI30</StyledToggleButton>
                <StyledToggleButton value="EI60">EI60</StyledToggleButton>
                <StyledToggleButton value="EI90">EI90</StyledToggleButton>
                <StyledToggleButton value="EI120">EI120</StyledToggleButton>
              </ToggleButtonGroup>

              <FormControlLabel
                control={
                  <Switch
                    checked={has3D}
                    onChange={(e) => setHas3D(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ViewInAr fontSize="small" />
                    <span>{t("products.filter.only3d")}</span>
                  </Box>
                }
              />

              <Box sx={{ flex: 1 }} />

              {activeFiltersCount > 0 && (
                <Button
                  onClick={clearFilters}
                  startIcon={<Clear />}
                  variant="outlined"
                  size="small"
                >
                  {t("common.clearFilters")}
                </Button>
              )}
            </Stack>
          </Stack>
        </FilterPaper>

        {/* Results Summary */}
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("products.results.found", { count: filtered.length })}
          </Typography>
          {filtered.length === 0 && (
            <Typography color="text.secondary">
              {t("products.empty.subtitle")}
            </Typography>
          )}
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {filtered.map((product, index) => (
            <Grid
              key={product.id}
              item
              xs={12}
              sm={viewMode === "list" ? 12 : 6}
              md={viewMode === "list" ? 12 : 4}
            >
              <Fade in timeout={300 + index * 50}>
                <Box
                  onClick={() => setSelected(product)}
                  sx={{ cursor: "pointer", height: "100%" }}
                >
                  <ProductCard
                    product={{ ...product, showPrice: false }}
                    viewMode={viewMode}
                  />
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {filtered.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {t("products.empty.title")}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t("products.empty.subtitle")}
            </Typography>
            <Button variant="contained" onClick={clearFilters}>
              {t("common.clearFilters")}
            </Button>
          </Box>
        )}
      </Container>

      <ProductModal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        product={selected}
      />
    </Box>
  );
};

export default Products;
